import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  Tag,
  TagCloseButton,
  TagLabel,
  Textarea,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { type DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { useState } from 'react';

import DevSandboxTooltip from 'components/DevSandbox/DevSandboxTooltip';

import { CVEntry, CVSectionType } from '../types';

const DRAG_GRIP_ICON = (
  <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24">
    <circle cx="8" cy="4" r="2" />
    <circle cx="16" cy="4" r="2" />
    <circle cx="8" cy="12" r="2" />
    <circle cx="16" cy="12" r="2" />
    <circle cx="8" cy="20" r="2" />
    <circle cx="16" cy="20" r="2" />
  </svg>
);

interface EntryFormProps {
  entry: CVEntry;
  sectionType: CVSectionType;
  onChange: (field: keyof CVEntry, value: string | string[]) => void;
  onRemove: () => void;
  canRemove: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  onEnterAdd?: () => void;
}

/** Left-side drag gutter for all entry types */
const CardDragGutter = ({ dragHandleProps }: { dragHandleProps?: DraggableProvidedDragHandleProps | null }) => {
  if (!dragHandleProps) return null;
  return (
    <Box
      {...dragHandleProps}
      display="flex"
      alignItems="center"
      px={2}
      cursor="grab"
      color="whiteAlpha.300"
      _hover={{ color: 'whiteAlpha.500' }}
      flexShrink={0}
      alignSelf="stretch"
    >
      {DRAG_GRIP_ICON}
    </Box>
  );
};

const EntryForm = ({ entry, sectionType, onChange, onRemove, canRemove, dragHandleProps, onEnterAdd }: EntryFormProps) => {
  const [tagInput, setTagInput] = useState('');
  const isSkillsOrLanguages = sectionType === 'skills' || sectionType === 'languages';
  const isHobbiesOrInterests = sectionType === 'hobbies' || sectionType === 'interests';
  const isCertifications = sectionType === 'certifications';
  const isProjects = sectionType === 'projects';

  const handleEnterAdd = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && onEnterAdd) {
      e.preventDefault();
      onEnterAdd();
    }
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();

    if (trimmed && !entry.tags.includes(trimmed)) {
      onChange('tags', [...entry.tags, trimmed]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    onChange(
      'tags',
      entry.tags.filter((t) => t !== tag)
    );
  };

  if (isSkillsOrLanguages) {
    return (
      <HStack
        spacing={0}
        borderRadius="md"
        backgroundColor="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.100"
        overflow="hidden"
        alignItems="stretch"
      >
        <CardDragGutter dragHandleProps={dragHandleProps} />
        <HStack spacing={2} flex={1} px={3} py={2}>
          <Input
            value={entry.title}
            onChange={(e) => onChange('title', e.target.value)}
            placeholder={sectionType === 'skills' ? 'e.g. React' : 'e.g. English'}
            variant="filled"
            size="sm"
            flex={1}
            onKeyDown={handleEnterAdd}
          />
          <Input
            value={entry.subtitle}
            onChange={(e) => onChange('subtitle', e.target.value)}
            placeholder={sectionType === 'skills' ? 'Category (e.g. Frontend)' : 'Level (e.g. Native)'}
            variant="filled"
            size="sm"
            width="160px"
            onKeyDown={handleEnterAdd}
          />
          {canRemove && (
            <DevSandboxTooltip label="Remove">
              <IconButton
                aria-label="Remove entry"
                icon={<CloseIcon boxSize={2} />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={onRemove}
              />
            </DevSandboxTooltip>
          )}
        </HStack>
      </HStack>
    );
  }

  if (isHobbiesOrInterests) {
    return (
      <HStack
        spacing={0}
        borderRadius="md"
        backgroundColor="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.100"
        overflow="hidden"
        alignItems="stretch"
      >
        <CardDragGutter dragHandleProps={dragHandleProps} />
        <HStack spacing={2} flex={1} px={3} py={2}>
          <Input
            value={entry.title}
            onChange={(e) => onChange('title', e.target.value)}
            placeholder={sectionType === 'hobbies' ? 'e.g. Photography' : 'e.g. Machine Learning'}
            variant="filled"
            size="sm"
            flex={1}
            onKeyDown={handleEnterAdd}
          />
          {canRemove && (
            <DevSandboxTooltip label="Remove">
              <IconButton
                aria-label="Remove entry"
                icon={<CloseIcon boxSize={2} />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={onRemove}
              />
            </DevSandboxTooltip>
          )}
        </HStack>
      </HStack>
    );
  }

  if (isCertifications) {
    return (
      <HStack
        spacing={0}
        borderRadius="md"
        backgroundColor="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.100"
        overflow="hidden"
        alignItems="stretch"
      >
        <CardDragGutter dragHandleProps={dragHandleProps} />
        <Box p={3} flex={1}>
          <VStack spacing={3} alignItems="stretch">
            <HStack justifyContent="space-between" alignItems="flex-start">
              <SimpleGrid columns={[1, 2]} gap={2} flex={1}>
                <FormControl>
                  <FormLabel fontSize="xs" color="whiteAlpha.500">
                    Certification Name
                  </FormLabel>
                  <Input
                    value={entry.title}
                    onChange={(e) => onChange('title', e.target.value)}
                    placeholder="AWS Solutions Architect"
                    variant="filled"
                    size="sm"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" color="whiteAlpha.500">
                    Issuing Organization
                  </FormLabel>
                  <Input
                    value={entry.subtitle}
                    onChange={(e) => onChange('subtitle', e.target.value)}
                    placeholder="Amazon Web Services"
                    variant="filled"
                    size="sm"
                  />
                </FormControl>
              </SimpleGrid>
              {canRemove && (
                <Box mt={6}>
                  <DevSandboxTooltip label="Remove entry">
                    <IconButton
                      aria-label="Remove entry"
                      icon={<CloseIcon boxSize={2} />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={onRemove}
                    />
                  </DevSandboxTooltip>
                </Box>
              )}
            </HStack>

            <SimpleGrid columns={[1, 2]} gap={2}>
              <FormControl>
                <FormLabel fontSize="xs" color="whiteAlpha.500">
                  Issue Date
                </FormLabel>
                <Input
                  value={entry.startDate}
                  onChange={(e) => onChange('startDate', e.target.value)}
                  placeholder="Jan 2023"
                  variant="filled"
                  size="sm"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" color="whiteAlpha.500">
                  Expiry Date (optional)
                </FormLabel>
                <Input
                  value={entry.endDate}
                  onChange={(e) => onChange('endDate', e.target.value)}
                  placeholder="Jan 2026 or No Expiry"
                  variant="filled"
                  size="sm"
                />
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={[1, 2]} gap={2}>
              <FormControl>
                <FormLabel fontSize="xs" color="whiteAlpha.500">
                  Credential ID (optional)
                </FormLabel>
                <Input
                  value={entry.location}
                  onChange={(e) => onChange('location', e.target.value)}
                  placeholder="ABC123XYZ"
                  variant="filled"
                  size="sm"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" color="whiteAlpha.500">
                  Credential URL (optional)
                </FormLabel>
                <Input
                  value={entry.url}
                  onChange={(e) => onChange('url', e.target.value)}
                  placeholder="https://verify.example.com/cert/123"
                  variant="filled"
                  size="sm"
                  onKeyDown={handleEnterAdd}
                />
              </FormControl>
            </SimpleGrid>
          </VStack>
        </Box>
      </HStack>
    );
  }

  // Projects: simplified — title, description, tech stack, link
  if (isProjects) {
    return (
      <HStack
        spacing={0}
        borderRadius="md"
        backgroundColor="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.100"
        overflow="hidden"
        alignItems="stretch"
      >
        <CardDragGutter dragHandleProps={dragHandleProps} />
        <Box p={3} flex={1}>
          <VStack spacing={3} alignItems="stretch">
            <HStack justifyContent="space-between" alignItems="flex-start">
              <FormControl flex={1}>
                <FormLabel fontSize="xs" color="whiteAlpha.500">
                  Project Name
                </FormLabel>
                <Input
                  value={entry.title}
                  onChange={(e) => onChange('title', e.target.value)}
                  placeholder="My Awesome Project"
                  variant="filled"
                  size="sm"
                />
              </FormControl>
              {canRemove && (
                <Box mt={6}>
                  <DevSandboxTooltip label="Remove entry">
                    <IconButton
                      aria-label="Remove entry"
                      icon={<CloseIcon boxSize={2} />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={onRemove}
                    />
                  </DevSandboxTooltip>
                </Box>
              )}
            </HStack>

            <FormControl>
              <FormLabel fontSize="xs" color="whiteAlpha.500">
                Description
              </FormLabel>
              <Textarea
                value={entry.description}
                onChange={(e) => onChange('description', e.target.value)}
                placeholder="What does this project do? What was your role?"
                variant="filled"
                size="sm"
                rows={2}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="whiteAlpha.500">
                Tech Stack
              </FormLabel>
              <HStack spacing={2}>
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a technology..."
                  variant="filled"
                  size="sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
              </HStack>
              {entry.tags.length > 0 && (
                <Wrap mt={2} spacing={1}>
                  {entry.tags.map((tag) => (
                    <WrapItem key={tag}>
                      <Tag size="sm" colorScheme="purple" variant="subtle">
                        <TagLabel>{tag}</TagLabel>
                        <TagCloseButton onClick={() => handleRemoveTag(tag)} />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
            </FormControl>

            <SimpleGrid columns={[1, 2]} gap={2}>
              <FormControl>
                <FormLabel fontSize="xs" color="whiteAlpha.500">
                  Repository URL
                </FormLabel>
                <Input
                  value={entry.url}
                  onChange={(e) => onChange('url', e.target.value)}
                  placeholder="https://github.com/user/repo"
                  variant="filled"
                  size="sm"
                />
                <Input
                  value={entry.urlLabel || ''}
                  onChange={(e) => onChange('urlLabel', e.target.value)}
                  placeholder="GitHub"
                  variant="filled"
                  size="sm"
                  mt={1}
                />
                <Box style={{ fontSize: '10px', color: 'var(--chakra-colors-whiteAlpha-400)', marginTop: '2px' }}>
                  Link label on CV (default: GitHub)
                </Box>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" color="whiteAlpha.500">
                  Live Preview URL
                </FormLabel>
                <Input
                  value={entry.previewUrl}
                  onChange={(e) => onChange('previewUrl', e.target.value)}
                  placeholder="https://my-project.com"
                  variant="filled"
                  size="sm"
                />
                <Input
                  value={entry.previewUrlLabel || ''}
                  onChange={(e) => onChange('previewUrlLabel', e.target.value)}
                  placeholder="Live Demo"
                  variant="filled"
                  size="sm"
                  mt={1}
                  onKeyDown={handleEnterAdd}
                />
                <Box style={{ fontSize: '10px', color: 'var(--chakra-colors-whiteAlpha-400)', marginTop: '2px' }}>
                  Link label on CV (default: Live Demo)
                </Box>
              </FormControl>
            </SimpleGrid>
          </VStack>
        </Box>
      </HStack>
    );
  }

  // Default: experience, education
  return (
    <HStack
      spacing={0}
      borderRadius="md"
      backgroundColor="whiteAlpha.50"
      border="1px solid"
      borderColor="whiteAlpha.100"
      overflow="hidden"
      alignItems="stretch"
    >
      <CardDragGutter dragHandleProps={dragHandleProps} />
      <Box p={3} flex={1}>
        <VStack spacing={3} alignItems="stretch">
          <HStack justifyContent="space-between" alignItems="flex-start">
            <SimpleGrid columns={[1, 2]} gap={2} flex={1}>
              <FormControl>
                <FormLabel fontSize="xs" color="whiteAlpha.500">
                  {sectionType === 'education' ? 'Degree / Program' : 'Title / Role'}
                </FormLabel>
                <Input
                  value={entry.title}
                  onChange={(e) => onChange('title', e.target.value)}
                  placeholder={sectionType === 'education' ? 'B.S. Computer Science' : 'Senior Developer'}
                  variant="filled"
                  size="sm"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" color="whiteAlpha.500">
                  {sectionType === 'education' ? 'Institution' : 'Company / Organization'}
                </FormLabel>
                <Input
                  value={entry.subtitle}
                  onChange={(e) => onChange('subtitle', e.target.value)}
                  placeholder={sectionType === 'education' ? 'MIT' : 'Acme Corp'}
                  variant="filled"
                  size="sm"
                />
              </FormControl>
            </SimpleGrid>
            {canRemove && (
              <Box mt={6}>
                <DevSandboxTooltip label="Remove entry">
                  <IconButton
                    aria-label="Remove entry"
                    icon={<CloseIcon boxSize={2} />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={onRemove}
                  />
                </DevSandboxTooltip>
              </Box>
            )}
          </HStack>

          <SimpleGrid columns={[1, 3]} gap={2}>
            <FormControl>
              <FormLabel fontSize="xs" color="whiteAlpha.500">
                Location
              </FormLabel>
              <Input
                value={entry.location}
                onChange={(e) => onChange('location', e.target.value)}
                placeholder="City, Country"
                variant="filled"
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="whiteAlpha.500">
                Start Date
              </FormLabel>
              <Input
                value={entry.startDate}
                onChange={(e) => onChange('startDate', e.target.value)}
                placeholder="Jan 2022"
                variant="filled"
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="whiteAlpha.500">
                End Date
              </FormLabel>
              <Input
                value={entry.endDate}
                onChange={(e) => onChange('endDate', e.target.value)}
                placeholder="Present"
                variant="filled"
                size="sm"
                onKeyDown={handleEnterAdd}
              />
            </FormControl>
          </SimpleGrid>

          <FormControl>
            <FormLabel fontSize="xs" color="whiteAlpha.500">
              Description
            </FormLabel>
            <Textarea
              value={entry.description}
              onChange={(e) => onChange('description', e.target.value)}
              placeholder={"• Led a team of 5 developers...\n• Increased performance by 40%...\n• Built and deployed..."}
              variant="filled"
              size="sm"
              rows={3}
            />
          </FormControl>

          {sectionType !== 'education' && (
            <FormControl>
              <FormLabel fontSize="xs" color="whiteAlpha.500">
                Tags / Technologies
              </FormLabel>
              <HStack spacing={2}>
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag..."
                  variant="filled"
                  size="sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
              </HStack>
              {entry.tags.length > 0 && (
                <Wrap mt={2} spacing={1}>
                  {entry.tags.map((tag) => (
                    <WrapItem key={tag}>
                      <Tag size="sm" colorScheme="purple" variant="subtle">
                        <TagLabel>{tag}</TagLabel>
                        <TagCloseButton onClick={() => handleRemoveTag(tag)} />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
            </FormControl>
          )}
        </VStack>
      </Box>
    </HStack>
  );
};

export default EntryForm;
